import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { AdminRunner } from '@/core/types';

interface RunnerDetailModalProps {
  runner: AdminRunner | null;
  onClose: () => void;
}

export function RunnerDetailModal({ runner, onClose }: RunnerDetailModalProps) {
  if (!runner) return null;

  const isUnregistered = runner.timing && runner.timing.length > 0 && !runner.email;

  return (
    <Dialog open={!!runner} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ランナー詳細
          {isUnregistered && <Chip label="未登録" color="error" size="small" />}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* 基本情報 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            基本情報
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600, width: 120 }}>
                  ゼッケン
                </TableCell>
                <TableCell>{runner.bib}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600 }}>
                  氏名
                </TableCell>
                <TableCell>{runner.fullName || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600 }}>
                  カナ
                </TableCell>
                <TableCell>{runner.fullName_kana || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600 }}>
                  生年月日
                </TableCell>
                <TableCell>{runner.birth || '-'}</TableCell>
              </TableRow>
              {runner.tempFullName && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 600 }}>
                    仮名
                  </TableCell>
                  <TableCell>
                    <Chip label={runner.tempFullName} size="small" color="warning" variant="outlined" />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600 }}>
                  メール
                </TableCell>
                <TableCell>{runner.email || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 600 }}>
                  ID
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {runner.id}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* 計測データ */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            計測データ ({runner.timing?.length || 0}件)
          </Typography>
          {runner.timing && runner.timing.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>地点ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>タイム</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runner.timing.map((entry) => (
                  <TableRow key={entry.locationId}>
                    <TableCell>{entry.locationId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              計測データがありません
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
